import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { DAY_END, DAY_START, DRAG_TYPE, HOUR_PX, TOTAL_PX, hm, timeOf, toMin, topOf } from "@/lib/cal";
import { cn } from "@/lib/utils";

export const startDrag = (e, a, offsetMin = 0) => {
  e.stopPropagation();
  e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ id: a.id, offsetMin }));
  e.dataTransfer.effectAllowed = "move";
};

export const useDrop = (onDrop) => {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (e) => {
        if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over) setOver(true);
      },
      onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); },
      onDrop: (e) => {
        e.preventDefault();
        setOver(false);
        const raw = e.dataTransfer.getData(DRAG_TYPE);
        if (raw) onDrop(JSON.parse(raw), e);
      },
    },
  };
};

const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);

export const TimeGutter = () => (
  <div className="sticky left-0 z-20 w-14 shrink-0 border-r bg-card">
    <div className="sticky top-0 z-30 h-14 border-b bg-card" />
    <div className="relative" style={{ height: TOTAL_PX }}>
      {HOURS.map((h) => (
        <span key={h} className="absolute right-2 -translate-y-1/2 font-mono text-[10px] text-muted-foreground" style={{ top: (h - DAY_START) * HOUR_PX }}>
          {h > DAY_START && hm(h * 60)}
        </span>
      ))}
    </div>
  </div>
);

export const HourLines = () => (
  <div className="pointer-events-none absolute inset-0"
    style={{ backgroundImage: `linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.4) 1px, transparent 1px)`,
      backgroundSize: `100% ${HOUR_PX}px, 100% ${HOUR_PX / 2}px` }} />
);

const STRIPES = { backgroundImage: "repeating-linear-gradient(135deg, hsl(var(--muted) / 0.9) 0 6px, transparent 6px 12px)" };

export const OffShade = ({ status }) => {
  if (!status) return null;
  if (status.off)
    return (
      <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-4" style={STRIPES}>
        <span className="rounded-full border bg-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{status.label}</span>
      </div>
    );
  return (
    <>
      <div className="pointer-events-none absolute inset-x-0 top-0 bg-muted/60" style={{ height: Math.max(topOf(status.start), 0) }} />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-muted/60" style={{ top: Math.min(topOf(status.end), TOTAL_PX) }} />
    </>
  );
};

export const EventBlock = ({ a, lane = 0, lanes = 1, color = "#00B3C7", onClick, showStaff }) => {
  const top = Math.max(topOf(toMin(timeOf(a.start))), 0);
  const height = Math.max((a.duration / 60) * HOUR_PX - 2, 20);
  const canDrag = a.status !== "anulat";
  return (
    <button
      data-testid={`appt-${a.id}`}
      draggable={canDrag}
      onDragStart={(e) => startDrag(e, a, ((e.clientY - e.currentTarget.getBoundingClientRect().top) / HOUR_PX) * 60)}
      onClick={(e) => { e.stopPropagation(); onClick(a); }}
      style={{ top, height, left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`, backgroundColor: `${color}2E`, borderLeftColor: color }}
      className={cn("absolute z-10 overflow-hidden rounded-md border-l-4 px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition-[filter,box-shadow] hover:z-20 hover:shadow-md hover:brightness-110",
        canDrag && "cursor-grab active:cursor-grabbing", a.status === "anulat" && "opacity-50 line-through")}
    >
      <div className="flex items-center gap-1 font-semibold">
        {a.status === "finalizat" && <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />}
        <span className="truncate">{timeOf(a.start)} {a.service_name}</span>
      </div>
      {height > 34 && <div className="truncate text-muted-foreground">{showStaff ? a.staff_name : a.client_name || "—"}</div>}
    </button>
  );
};
